import type { TechlineCaseEntity } from '@packages/domain/techline/cases';

import { TextareaHelper, TextInputHelper } from '@packages/ui/shared';
import { useCallback, useEffect, useState } from 'react';
import { Form } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { createUseStyles } from 'react-jss';

const useStyles = createUseStyles({
    flexOne: {
        flex: 1,
    },
    checkbox: {
        fontWeight: 400,
        fontSize: 15,
    },
    checkboxSectionStyle: {
        backgroundColor: '#F8F8F8',
        marginTop: 10,
        marginLeft: -3,
        paddingTop: 20,
        paddingLeft: 25,
        paddingBottom: 20,
        borderRadius: 5,
    },
});

/** Props for the ConcernDetailsCheckboxes component. */
type ConcernDetailsCheckboxesProps = {
    /** The current case details used to populate checkbox and sub-field values. */
    caseDetails: TechlineCaseEntity;

    /** Field-level errors computed by the validation hook (concern details step errors). */
    errors: Record<string, string>;

    /** Callback to propagate partial case detail changes to the parent. */
    onCaseDetailsChange: (changes: Partial<TechlineCaseEntity>) => void;

    /** Callback to mark a field as touched in the parent's tracking state. */
    onTouched: (field: string) => void;

    /** Map of field names to whether the user has interacted with them (controls error display). */
    touched: Record<string, boolean>;
};

/**
 * Renders the concern details checkbox group (duplicate concern, vehicle been in before,
 * parts replaced) with conditional sub-fields for each checked option.
 *
 * @param props - component props
 * @returns the concern details checkboxes section
 */
export function ConcernDetailsCheckboxes({
    caseDetails,
    errors,
    onCaseDetailsChange,
    onTouched,
    touched,
}: ConcernDetailsCheckboxesProps) {
    const { t } = useTranslation();
    const classes = useStyles();

    const [concernCheckboxDetails, setConcernCheckboxDetails] = useState({
        hasBeenAbleToDuplicate: caseDetails?.hasBeenAbleToDuplicate === true,
        vehicleHasBeenInBefore: caseDetails?.vehicleHasBeenInBefore === true,
        werePartsReplaced: caseDetails?.werePartsReplaced === true,
        technician5StepsBeforeFailureDuplication: caseDetails?.technician5StepsBeforeFailureDuplication || '',
        previousRONumber: caseDetails?.previousRONumber || '',
        previousMileage: caseDetails?.previousMileage
            ? Number(caseDetails.previousMileage).toLocaleString('en-US')
            : '',
        previousDiagnosisAndRepairs: caseDetails?.previousDiagnosisAndRepairs || '',
        whatPartsWereReplaced: caseDetails?.whatPartsWereReplaced || '',
        didTheConditionImprove: caseDetails?.didTheConditionImprove || '',
    });

    // Forwarded callback aliases
    const handleCaseDetailsChange = onCaseDetailsChange;

    /**
     * Handles blur on a checkbox sub-field by trimming, marking touched, and re-validating.
     *
     * @param field - the field name that lost focus
     */
    const handleBlur = useCallback(
        (field: string) => {
            const currentValue = concernCheckboxDetails[field as keyof typeof concernCheckboxDetails];
            if (typeof currentValue === 'string') {
                const trimmedValue = currentValue.trim();
                setConcernCheckboxDetails((prev) => ({ ...prev, [field]: trimmedValue }));
                handleCaseDetailsChange({ [field]: trimmedValue });
            }
            onTouched(field);
        },
        [concernCheckboxDetails, handleCaseDetailsChange, onTouched]
    );

    // Handlers

    /** Toggles the "has been able to duplicate" checkbox and notifies parent. */
    const handleDuplicateConcernCheckboxChange = useCallback(() => {
        const newValue = !concernCheckboxDetails.hasBeenAbleToDuplicate;
        setConcernCheckboxDetails((prev) => ({
            ...prev,
            hasBeenAbleToDuplicate: newValue,
        }));
        handleCaseDetailsChange({ hasBeenAbleToDuplicate: newValue });
    }, [concernCheckboxDetails.hasBeenAbleToDuplicate, handleCaseDetailsChange]);

    /** Handles text changes in the steps-to-duplicate textarea. */
    const handleStepsToDuplicateTextChange = useCallback((text: string) => {
        setConcernCheckboxDetails((prev) => ({
            ...prev,
            technician5StepsBeforeFailureDuplication: text,
        }));
    }, []);

    /** Handles blur on the steps-to-duplicate field. */
    const handleStepsToDuplicateBlur = useCallback(() => {
        handleBlur('technician5StepsBeforeFailureDuplication');
    }, [handleBlur]);

    /** Toggles the "vehicle has been in before" checkbox and notifies parent. */
    const handleVehicleBeenInBeforeCheckboxChange = useCallback(() => {
        const newValue = !concernCheckboxDetails.vehicleHasBeenInBefore;
        setConcernCheckboxDetails((prev) => ({
            ...prev,
            vehicleHasBeenInBefore: newValue,
        }));
        handleCaseDetailsChange({ vehicleHasBeenInBefore: newValue });
    }, [concernCheckboxDetails.vehicleHasBeenInBefore, handleCaseDetailsChange]);

    /** Handles text changes in the previous RO number field. */
    const handlePreviousRONumberTextChange = useCallback((text: string) => {
        setConcernCheckboxDetails((prev) => ({
            ...prev,
            previousRONumber: text,
        }));
    }, []);

    /** Handles blur on the previous RO number field. */
    const handlePreviousRONumberBlur = useCallback(() => {
        handleBlur('previousRONumber');
    }, [handleBlur]);

    /** Handles text changes in the previous mileage field. */
    const handlePreviousMileageTextChange = useCallback((text: string) => {
        const formatted = formatMileage(text);
        setConcernCheckboxDetails((prev) => ({
            ...prev,
            previousMileage: formatted,
        }));
    }, []);

    /** Handles blur on the previous mileage field. */
    const handlePreviousMileageBlur = useCallback(() => {
        const trimmed = concernCheckboxDetails.previousMileage.trim();
        setConcernCheckboxDetails((prev) => ({ ...prev, previousMileage: trimmed }));
        handleCaseDetailsChange({ previousMileage: Number(trimmed.replace(/\D/g, '')) || 0 });
        onTouched('previousMileage');
    }, [concernCheckboxDetails.previousMileage, handleCaseDetailsChange, onTouched]);

    /** Handles text changes in the previous diagnosis textarea. */
    const handlePreviousDiagnosisTextChange = useCallback((text: string) => {
        setConcernCheckboxDetails((prev) => ({
            ...prev,
            previousDiagnosisAndRepairs: text,
        }));
    }, []);

    /** Handles blur on the previous diagnosis field. */
    const handlePreviousDiagnosisBlur = useCallback(() => {
        handleBlur('previousDiagnosisAndRepairs');
    }, [handleBlur]);

    /** Toggles the "were parts replaced" checkbox and notifies parent. */
    const handlePartsReplacedCheckboxChange = useCallback(() => {
        const newValue = !concernCheckboxDetails.werePartsReplaced;
        setConcernCheckboxDetails((prev) => ({
            ...prev,
            werePartsReplaced: newValue,
        }));
        handleCaseDetailsChange({ werePartsReplaced: newValue });
    }, [concernCheckboxDetails.werePartsReplaced, handleCaseDetailsChange]);

    /** Handles text changes in the "what parts were replaced" field. */
    const handleWhatPartsReplacedTextChange = useCallback((text: string) => {
        setConcernCheckboxDetails((prev) => ({
            ...prev,
            whatPartsWereReplaced: text,
        }));
    }, []);

    /** Handles blur on the "what parts were replaced" field. */
    const handleWhatPartsReplacedBlur = useCallback(() => {
        handleBlur('whatPartsWereReplaced');
    }, [handleBlur]);

    /** Handles text changes in the "did the condition improve" textarea. */
    const handleConditionImproveTextChange = useCallback((text: string) => {
        setConcernCheckboxDetails((prev) => ({
            ...prev,
            didTheConditionImprove: text,
        }));
    }, []);

    /** Handles blur on the "did the condition improve" field. */
    const handleConditionImproveBlur = useCallback(() => {
        handleBlur('didTheConditionImprove');
    }, [handleBlur]);

    // Effects

    /**
     * Syncs local concern detail state when the parent case details change.
     */
    useEffect(() => {
        setConcernCheckboxDetails({
            hasBeenAbleToDuplicate: caseDetails?.hasBeenAbleToDuplicate === true,
            vehicleHasBeenInBefore: caseDetails?.vehicleHasBeenInBefore === true,
            werePartsReplaced: caseDetails?.werePartsReplaced === true,
            technician5StepsBeforeFailureDuplication: caseDetails?.technician5StepsBeforeFailureDuplication || '',
            previousRONumber: caseDetails?.previousRONumber || '',
            previousMileage: caseDetails?.previousMileage
                ? Number(caseDetails.previousMileage).toLocaleString('en-US')
                : '',
            previousDiagnosisAndRepairs: caseDetails?.previousDiagnosisAndRepairs || '',
            whatPartsWereReplaced: caseDetails?.whatPartsWereReplaced || '',
            didTheConditionImprove: caseDetails?.didTheConditionImprove || '',
        });
    }, [caseDetails]);

    return (
        <>
            <div className="flex-column mb-10 mt-5 pr-5">
                <Form.Check
                    checked={concernCheckboxDetails.hasBeenAbleToDuplicate}
                    className={`${classes.checkbox} mt-10`}
                    id="duplicateConcern"
                    label={t('techline:concernDetails.checkboxLabels.duplicateConcern')}
                    onChange={handleDuplicateConcernCheckboxChange}
                    type="checkbox"
                />
                {concernCheckboxDetails.hasBeenAbleToDuplicate && (
                    <div className={`${classes.checkboxSectionStyle}`}>
                        <TextareaHelper
                            errorMessage={
                                touched.technician5StepsBeforeFailureDuplication
                                    ? t(errors.technician5StepsBeforeFailureDuplication)
                                    : ''
                            }
                            label={t('techline:concernDetails.stepsToDuplicateTextArea.label')}
                            labelRightElement={
                                <span style={charCountStyle}>
                                    {`${
                                        concernCheckboxDetails.technician5StepsBeforeFailureDuplication.trim().length
                                    }/2000`}
                                </span>
                            }
                            labelStyle={labelStyle}
                            maxLength={2000}
                            onBlur={handleStepsToDuplicateBlur}
                            onChangeText={handleStepsToDuplicateTextChange}
                            placeholder={t('techline:concernDetails.stepsToDuplicateTextArea.placeholder')}
                            required
                            style={textareaPaddingStyle}
                            textInputStyle={whiteInputStyle}
                            value={concernCheckboxDetails.technician5StepsBeforeFailureDuplication}
                        />
                    </div>
                )}
                <Form.Check
                    checked={concernCheckboxDetails.vehicleHasBeenInBefore}
                    id="concernBefore"
                    label={t('techline:concernDetails.checkboxLabels.concernBefore')}
                    onChange={handleVehicleBeenInBeforeCheckboxChange}
                    style={concernBeforeCheckboxStyle}
                    type="checkbox"
                />
                {concernCheckboxDetails.vehicleHasBeenInBefore && (
                    <div className={`${classes.checkboxSectionStyle}`}>
                        <div className={`d-flex flex-row`}>
                            <div className={`${classes.flexOne} mr-5`}>
                                <TextInputHelper
                                    errorMessage={touched.previousRONumber ? t(errors.previousRONumber) : ''}
                                    inputContainerStyle={whiteBackgroundStyle}
                                    label={t('techline:concernDetails.previousRepairOrderNumberText.label')}
                                    labelStyle={labelStyle}
                                    onBlur={handlePreviousRONumberBlur}
                                    onChangeText={handlePreviousRONumberTextChange}
                                    placeholder={t('techline:concernDetails.previousRepairOrderNumberText.placeholder')}
                                    required
                                    textInputStyle={whiteInputStyle}
                                    type="text"
                                    value={concernCheckboxDetails.previousRONumber}
                                />
                            </div>
                            <div className={`${classes.flexOne} mr-5`}>
                                <TextInputHelper
                                    errorMessage={touched.previousMileage ? t(errors.previousMileage) : ''}
                                    inputContainerStyle={whiteBackgroundStyle}
                                    keyboardType="numeric"
                                    label={t('techline:concernDetails.previousMileageText.label')}
                                    labelStyle={labelStyle}
                                    onBlur={handlePreviousMileageBlur}
                                    onChangeText={handlePreviousMileageTextChange}
                                    placeholder={t('techline:concernDetails.previousMileageText.placeholder')}
                                    required
                                    textInputStyle={whiteInputStyle}
                                    value={concernCheckboxDetails.previousMileage}
                                />
                            </div>
                        </div>
                        <div className={`mr-5 mt-5`}>
                            <TextareaHelper
                                errorMessage={
                                    touched.previousDiagnosisAndRepairs ? t(errors.previousDiagnosisAndRepairs) : ''
                                }
                                label={t('techline:concernDetails.previousDiagnosisAndRepairTextArea.label')}
                                labelRightElement={
                                    <span style={charCountStyle}>
                                        {`${concernCheckboxDetails.previousDiagnosisAndRepairs.trim().length}/2000`}
                                    </span>
                                }
                                labelStyle={labelStyle}
                                maxLength={2000}
                                onBlur={handlePreviousDiagnosisBlur}
                                onChangeText={handlePreviousDiagnosisTextChange}
                                placeholder={t(
                                    'techline:concernDetails.previousDiagnosisAndRepairTextArea.placeholder'
                                )}
                                required
                                textInputStyle={whiteInputStyle}
                                value={concernCheckboxDetails.previousDiagnosisAndRepairs}
                            />
                        </div>
                    </div>
                )}
                <Form.Check
                    checked={concernCheckboxDetails.werePartsReplaced}
                    className={`${classes.checkbox} mt-5`}
                    id="partsReplaced"
                    label={t('techline:concernDetails.checkboxLabels.partsReplaced')}
                    onChange={handlePartsReplacedCheckboxChange}
                    type="checkbox"
                />
                {concernCheckboxDetails.werePartsReplaced && (
                    <div className={`${classes.checkboxSectionStyle}`}>
                        <div style={halfWidthStyle}>
                            <TextInputHelper
                                errorMessage={touched.whatPartsWereReplaced ? t(errors.whatPartsWereReplaced) : ''}
                                inputContainerStyle={whiteBackgroundStyle}
                                label={t('techline:concernDetails.partsReplacedText.label')}
                                labelStyle={labelStyle}
                                onBlur={handleWhatPartsReplacedBlur}
                                onChangeText={handleWhatPartsReplacedTextChange}
                                placeholder={t('techline:concernDetails.partsReplacedText.placeholder')}
                                required
                                textInputStyle={whiteInputStyle}
                                value={concernCheckboxDetails.whatPartsWereReplaced}
                            />
                        </div>
                        <div className="mr-5 mt-5">
                            <TextareaHelper
                                errorMessage={touched.didTheConditionImprove ? t(errors.didTheConditionImprove) : ''}
                                label={t('techline:concernDetails.conditionImproveTextArea.label')}
                                labelRightElement={
                                    <span style={charCountStyle}>
                                        {`${concernCheckboxDetails.didTheConditionImprove.trim().length}/2000`}
                                    </span>
                                }
                                labelStyle={labelStyle}
                                maxLength={2000}
                                onBlur={handleConditionImproveBlur}
                                onChangeText={handleConditionImproveTextChange}
                                placeholder={t('techline:concernDetails.conditionImproveTextArea.placeholder')}
                                required
                                textInputStyle={whiteInputStyle}
                                value={concernCheckboxDetails.didTheConditionImprove}
                            />
                        </div>
                    </div>
                )}
            </div>
        </>
    );
}

/**
 * Formats a raw mileage input string by stripping non-digits, capping at 7 digits,
 * and inserting commas as thousand separators.
 *
 * @param input - the raw input string
 * @returns the formatted mileage string
 * @example
 * ```ts
 * formatMileage('1234567') // '1,234,567'
 * formatMileage('12345678') // '1,234,567' (capped at 7 digits)
 * formatMileage('abc') // ''
 * ```
 */
function formatMileage(input: string): string {
    let digitsOnly = input.replace(/\D/g, '');

    if (!digitsOnly) {
        return '';
    }
    digitsOnly = digitsOnly.slice(0, 7);
    return digitsOnly.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
}

// Style constants

/** Character count label style used across multiple textarea fields. */
const charCountStyle = { color: '#4C525D', fontSize: 13, fontWeight: 400 } as const;

/** Shared label style with normal weight. */
const labelStyle = { fontWeight: 400 } as const;

/** Style for the "concern before" checkbox. */
const concernBeforeCheckboxStyle = { fontWeight: 400, marginTop: '20px', fontSize: '15' };

/** White background style for input containers. */
const whiteBackgroundStyle = { backgroundColor: '#FFF' };

/** White input style with rounded corners. */
const whiteInputStyle = { backgroundColor: '#FFF', borderRadius: 5 };

/** Padding style for the steps-to-duplicate textarea wrapper. */
const textareaPaddingStyle = { paddingRight: 20 };

/** Half-width container style. */
const halfWidthStyle = { width: '50%' };
